import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import type {
  CreateSubscriptionDto,
  OctopusCloudPlatformOptions,
  OctopusServerPlatformOptions,
  EvergreenLifecycleMetadata,
  TrialLifecycleMetadata,
  PaidLifecycleMetadata,
  ComplimentaryLifecycleMetadata,
} from "client/api-v2";
import { LifecycleType, ProductPlatform, ComplimentaryType } from "client/api-v2";
import { ErrorAlert } from "components/alert/ErrorAlert";
import { SubscriptionForm } from "../../components/SubscriptionForm";
import type { SubscriptionFormData } from "../../components/SubscriptionForm";
import { SubscriptionService } from "../../services/subscriptionService";
import { subscriptionRoutes } from "../../subscription-routes";

function resolveAndValidatePlanId(data: SubscriptionFormData): string {
  const resolvedPlanId = data.plan?.id || data.planId;
  if (!resolvedPlanId) {
    throw new Error("Plan could not be determined. The plan may still be loading — please try again.");
  }
  return resolvedPlanId;
}

function validateCloudFields(data: SubscriptionFormData): string | null {
  if (data.platform === ProductPlatform.OctopusCloud) {
    if (!data.dnsPrefix) return "DNS Prefix is required for Cloud subscriptions";
    if (!data.cloudRegion) return "Cloud Region is required for Cloud subscriptions";
    if (!data.outageWindowStartUtc) return "Maintenance Window Start Time is required for Cloud subscriptions";
    if (!data.outageWindowEndUtc) return "Maintenance Window End Time is required for Cloud subscriptions";
  }
  return null;
}

function buildPlatformConfig(data: SubscriptionFormData): OctopusCloudPlatformOptions | OctopusServerPlatformOptions {
  const validFromDate = DateTime.utc().startOf("day").toFormat("yyyy-MM-dd");

  if (data.platform === ProductPlatform.OctopusCloud) {
    if (!data.outageWindowStartUtc || !data.outageWindowEndUtc) {
      throw new Error("Maintenance window times are required for Cloud subscriptions");
    }

    return {
      type: data.platform,
      validFrom: validFromDate,
      cloudRegionId: data.cloudRegion?.id || "",
      dnsPrefix: data.dnsPrefix || "",
      outageWindowStartUtc: data.outageWindowStartUtc.toFormat("HH:mm:ss"),
      outageWindowEndUtc: data.outageWindowEndUtc.toFormat("HH:mm:ss"),
    };
  }

  if (!data.platform) {
    throw new Error("Platform is required");
  }
  return { type: data.platform, validFrom: validFromDate };
}

function validateTemporaryNewUserFields(data: SubscriptionFormData):
  | {
      resolvedPlanId: string;
      platformConfig: OctopusCloudPlatformOptions | OctopusServerPlatformOptions;
    }
  | string {
  if (!data.platform) {
    return "Platform is required";
  }
  if (!data.email) {
    return "Please select an existing user or provide new user details";
  }
  if (!data.firstName?.trim()) {
    return "First name is required for new users";
  }
  if (!data.lastName?.trim()) {
    return "Last name is required for new users";
  }

  const cloudError = validateCloudFields(data);
  if (cloudError) {
    return cloudError;
  }

  return {
    resolvedPlanId: resolveAndValidatePlanId(data),
    platformConfig: buildPlatformConfig(data),
  };
}

export function SubscriptionCreateForm() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [createLifecycleType, setCreateLifecycleType] = useState<LifecycleType>(LifecycleType.Evergreen);

  const handleCreateLifecycleTypeChange = (newType: LifecycleType) => {
    setCreateLifecycleType(newType);
    setError(null);
  };

  const validateCommonFields = (
    data: SubscriptionFormData
  ):
    | {
        ownerUserId: string;
        resolvedPlanId: string;
        platformConfig: OctopusCloudPlatformOptions | OctopusServerPlatformOptions;
      }
    | string => {
    if (!data.platform) {
      return "Platform is required";
    }

    if (!data.user || !data.user.id) {
      return "Subscription owner is required";
    }

    const cloudError = validateCloudFields(data);
    if (cloudError) {
      return cloudError;
    }

    return {
      ownerUserId: data.user.id,
      resolvedPlanId: resolveAndValidatePlanId(data),
      platformConfig: buildPlatformConfig(data),
    };
  };

  const withSubmitHandler = (
    handler: (data: SubscriptionFormData) => Promise<void>
  ): ((data: SubscriptionFormData) => Promise<void>) => {
    return async (data: SubscriptionFormData) => {
      setIsSubmitting(true);
      setError(null);
      try {
        await handler(data);
      } catch (err) {
        console.error("Error creating subscription:", err);
        setError(err);
      } finally {
        setIsSubmitting(false);
      }
    };
  };

  const handleEvergreenSubmit = withSubmitHandler(async (data) => {
    const validated = validateCommonFields(data);
    if (typeof validated === "string") {
      setError(new Error(validated));
      return;
    }

    const lifecycleMetadata: EvergreenLifecycleMetadata = {
      lifecycleType: LifecycleType.Evergreen,
      expireOnInactivity: false,
    };

    const createRequest: CreateSubscriptionDto = {
      planId: validated.resolvedPlanId,
      ownerUserId: validated.ownerUserId,
      friendlyName: data.friendlyName || undefined,
      platform: validated.platformConfig,
      lifecycle: lifecycleMetadata,
    };

    const newSubscription = await SubscriptionService.createSubscription(createRequest);
    navigate(subscriptionRoutes.detail(newSubscription.id || "").root);
  });

  const handleTrialSubmit = withSubmitHandler(async (data) => {
    const platform = data.platform;
    if (!platform || (platform !== ProductPlatform.OctopusCloud && platform !== ProductPlatform.OctopusServer)) {
      setError(new Error("Platform is required"));
      return;
    }

    if (!data.company) {
      setError(new Error("Company is required"));
      return;
    }

    const existingUserId = data.user?.id;
    if (!existingUserId && !data.email) {
      setError(new Error("Please select an existing user or provide new user details"));
      return;
    }

    if (!existingUserId) {
      if (!data.firstName?.trim()) {
        setError(new Error("First name is required for new users"));
        return;
      }
      if (!data.lastName?.trim()) {
        setError(new Error("Last name is required for new users"));
        return;
      }
    }

    const cloudError = validateCloudFields(data);
    if (cloudError) {
      setError(new Error(cloudError));
      return;
    }

    const resolvedPlanId = resolveAndValidatePlanId(data);
    const platformConfig = buildPlatformConfig(data);

    const lifecycleMetadata: TrialLifecycleMetadata = {
      lifecycleType: LifecycleType.Trial,
    };

    const createRequest: CreateSubscriptionDto = {
      planId: resolvedPlanId,
      ownerUserId: existingUserId || undefined,
      newUserFirstName: !existingUserId ? data.firstName : undefined,
      newUserLastName: !existingUserId ? data.lastName : undefined,
      newUserEmail: !existingUserId ? data.email : undefined,
      companyName: data.company,
      phone: data.phone || undefined,
      friendlyName: data.friendlyName || undefined,
      platform: platformConfig,
      lifecycle: lifecycleMetadata,
    };

    const newSubscription = await SubscriptionService.createSubscription(createRequest);
    navigate(subscriptionRoutes.detail(newSubscription.id || "").root);
  });

  const handlePaidSubmit = withSubmitHandler(async (data) => {
    const validated = validateCommonFields(data);
    if (typeof validated === "string") {
      setError(new Error(validated));
      return;
    }

    const lifecycleMetadata: PaidLifecycleMetadata = {
      lifecycleType: LifecycleType.Paid,
    };

    const createRequest: CreateSubscriptionDto = {
      planId: validated.resolvedPlanId,
      ownerUserId: validated.ownerUserId,
      friendlyName: data.friendlyName || undefined,
      platform: validated.platformConfig,
      lifecycle: lifecycleMetadata,
      zuoraSubscriptionKey: data.zuoraSubscriptionKey,
      termEndDate: data.termEndDate ? data.termEndDate.toISODate() : undefined,
    };

    const newSubscription = await SubscriptionService.createSubscription(createRequest);
    navigate(subscriptionRoutes.detail(newSubscription.id || "").root);
  });

  const handleComplimentarySubmit = withSubmitHandler(async (data) => {
    if (!data.complimentaryType) {
      setError(new Error("Complimentary Type is required"));
      return;
    }

    if (!data.termEndDate) {
      setError(new Error("Term End Date is required for complimentary subscriptions"));
      return;
    }

    const existingUserId = data.user?.id;
    const isTemporaryNewUserPath = data.complimentaryType === ComplimentaryType.Temporary && !existingUserId;

    const resolved = isTemporaryNewUserPath ? validateTemporaryNewUserFields(data) : validateCommonFields(data);
    if (typeof resolved === "string") {
      setError(new Error(resolved));
      return;
    }
    const { resolvedPlanId, platformConfig } = resolved;

    const lifecycleMetadata: ComplimentaryLifecycleMetadata = {
      lifecycleType: LifecycleType.Complimentary,
      complimentaryType: data.complimentaryType,
      autoRenewPeriod:
        data.complimentaryType === ComplimentaryType.Infrastructure ? data.autoRenewPeriod || undefined : undefined,
    };

    const createRequest: CreateSubscriptionDto = {
      planId: resolvedPlanId,
      ownerUserId: existingUserId ?? undefined,
      newUserFirstName: isTemporaryNewUserPath ? data.firstName : undefined,
      newUserLastName: isTemporaryNewUserPath ? data.lastName : undefined,
      newUserEmail: isTemporaryNewUserPath ? data.email : undefined,
      friendlyName: data.friendlyName || undefined,
      platform: platformConfig,
      lifecycle: lifecycleMetadata,
      termEndDate: data.termEndDate.toISODate() ?? undefined,
    };

    const newSubscription = await SubscriptionService.createSubscription(createRequest);
    navigate(subscriptionRoutes.detail(newSubscription.id || "").root);
  });

  const handleSubmit =
    createLifecycleType === LifecycleType.Trial
      ? handleTrialSubmit
      : createLifecycleType === LifecycleType.Paid
      ? handlePaidSubmit
      : createLifecycleType === LifecycleType.Complimentary
      ? handleComplimentarySubmit
      : handleEvergreenSubmit;

  const handleCancel = () => {
    navigate(subscriptionRoutes.root);
  };

  return (
    <Box>
      {error !== null && <ErrorAlert error={error} />}
      <SubscriptionForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={isSubmitting}
        onCreateLifecycleTypeChange={handleCreateLifecycleTypeChange}
      />
    </Box>
  );
}
